import type {
  GSplatMaterialTemplate,
  GSplatMaterialTemplateSnapshot,
} from '@/plugins/gsplat/effects/GSplatMaterialTemplate'
import type {
  GSplatMaterialLeaf,
  GSplatShaderLanguage,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'

type TemplateMaterialTarget = Pick<
  GSplatMaterialLeaf,
  | 'language'
  | 'revision'
  | 'getShaderChunks'
  | 'getDefine'
  | 'setDefine'
  | 'getParameter'
  | 'setParameter'
  | 'deleteParameter'
  | 'update'
>

export class GSplatMaterialTemplateLanguageError extends Error {
  readonly code = 'GSPLAT_MATERIAL_TEMPLATE_LANGUAGE_MISSING'

  constructor(
    readonly language: GSplatShaderLanguage,
    readonly chunk: string
  ) {
    super(`GSplat material template 缺少当前 ${language.toUpperCase()} shader chunk: ${chunk}`)
    this.name = 'GSplatMaterialTemplateLanguageError'
  }
}

interface AppliedState {
  readonly templateRevision: number
  readonly language: GSplatShaderLanguage
  readonly materialRevision: number
  readonly defines: ReadonlyMap<string, unknown>
  readonly parameters: ReadonlyMap<string, unknown>
  readonly chunks: ReadonlyMap<string, string>
}

function collectChunks(
  snapshot: GSplatMaterialTemplateSnapshot,
  language: GSplatShaderLanguage
): Map<string, string> {
  const result = new Map<string, string>()
  for (const [rawName, source] of snapshot.chunks) {
    const separator = rawName.indexOf(':')
    if (separator < 0) {
      result.set(rawName, source)
      continue
    }
    const namespace = rawName.slice(0, separator)
    if (namespace === language) {
      result.set(rawName.slice(separator + 1), source)
    }
  }
  return result
}

function mapFromEntries(entries: readonly (readonly [string, unknown])[]): Map<string, unknown> {
  return new Map(entries)
}

function assertLanguageChunks(
  snapshot: GSplatMaterialTemplateSnapshot,
  language: GSplatShaderLanguage
): void {
  const alternate = language === 'glsl' ? 'wgsl' : 'glsl'
  const chunks = new Map(snapshot.chunks)
  for (const chunk of ['gsplatModifyVS', 'gsplatModifyPS']) {
    if (chunks.has(chunk)) continue
    if (chunks.has(`${alternate}:${chunk}`) && !chunks.has(`${language}:${chunk}`)) {
      throw new GSplatMaterialTemplateLanguageError(language, chunk)
    }
  }
}

/**
 * 把 backend-neutral template 快照同步到一个实际 View/Manager material。
 *
 * 每个 target 独立记录已应用的 key/value，移除时不会误删其它 owner 后写入的内容。
 * 仅 code/define/chunk 变化调用 `material.update()`；uniform-only template revision 只写参数。
 */
export class GSplatMaterialTemplateSynchronizer {
  private readonly applied = new WeakMap<TemplateMaterialTarget, AppliedState>()

  sync(material: TemplateMaterialTarget, template: GSplatMaterialTemplate): boolean {
    const snapshot = template.captureSnapshot()
    const language = material.language
    assertLanguageChunks(snapshot, language)
    const previous = this.applied.get(material)
    if (
      previous?.templateRevision === snapshot.revision &&
      previous.language === language &&
      previous.materialRevision === material.revision
    ) {
      return false
    }

    const nextDefines = mapFromEntries(snapshot.defines)
    const nextParameters = mapFromEntries(snapshot.parameters)
    const nextChunks = collectChunks(snapshot, language)
    const chunks = material.getShaderChunks(language)
    const rollback = {
      defines: new Map<string, unknown>(),
      parameters: new Map<string, unknown>(),
      chunks: new Map<string, string | undefined>(),
    }

    const oldDefines = previous?.defines ?? new Map<string, unknown>()
    const oldParameters = previous?.parameters ?? new Map<string, unknown>()
    const oldChunks = previous?.chunks ?? new Map<string, string>()
    const codeChanged =
      !sameMap(oldDefines, nextDefines) ||
      !sameMap(oldChunks, nextChunks) ||
      (!previous && (nextDefines.size > 0 || nextChunks.size > 0))

    try {
      for (const name of new Set([...oldDefines.keys(), ...nextDefines.keys()])) {
        const current = material.getDefine(name)
        rollback.defines.set(name, current)
        if (nextDefines.has(name)) material.setDefine(name, nextDefines.get(name))
        else if (Object.is(current, oldDefines.get(name))) material.setDefine(name, undefined)
      }

      for (const name of new Set([...oldParameters.keys(), ...nextParameters.keys()])) {
        const current = material.getParameter(name)
        rollback.parameters.set(name, current)
        if (nextParameters.has(name)) material.setParameter(name, nextParameters.get(name))
        else if (Object.is(current, oldParameters.get(name))) material.deleteParameter(name)
      }

      for (const name of new Set([...oldChunks.keys(), ...nextChunks.keys()])) {
        const current = chunks.get(name)
        rollback.chunks.set(name, current)
        if (nextChunks.has(name)) chunks.set(name, nextChunks.get(name) as string)
        else if (Object.is(current, oldChunks.get(name))) chunks.delete(name)
      }

      if (codeChanged) {
        material.update()
      }

      this.applied.set(material, {
        templateRevision: snapshot.revision,
        language,
        materialRevision: material.revision,
        defines: nextDefines,
        parameters: nextParameters,
        chunks: nextChunks,
      })
      return true
    } catch (error) {
      for (const [name, value] of rollback.defines) material.setDefine(name, value)
      for (const [name, value] of rollback.parameters) {
        if (value === undefined) material.deleteParameter(name)
        else material.setParameter(name, value)
      }
      for (const [name, value] of rollback.chunks) {
        if (value === undefined) chunks.delete(name)
        else chunks.set(name, value)
      }
      throw error
    }
  }
}

const defaultSynchronizer = new GSplatMaterialTemplateSynchronizer()

export function syncGSplatMaterialTemplate(
  material: TemplateMaterialTarget,
  template: GSplatMaterialTemplate
): boolean {
  return defaultSynchronizer.sync(material, template)
}

function sameMap(a: ReadonlyMap<string, unknown>, b: ReadonlyMap<string, unknown>): boolean {
  if (a.size !== b.size) return false
  for (const [name, value] of a) {
    if (!b.has(name) || !Object.is(value, b.get(name))) return false
  }
  return true
}
